import { useClerk } from '@clerk/clerk-react';
import { useEffect } from 'react';

import { trpc } from '@/lib/api/trpc';

/** CRM V3 origin for each CRM V2 host that has one. Partner hosts, previews and local dev have none. */
const V3_ORIGIN_BY_HOST: Record<string, string> = {
  'app.tagada.io': 'https://v3.tagada.io',
  'app.tagada.dev': 'https://v3.tagada.dev',
};

export function crmV3OriginFor(hostname: string): string | null {
  return V3_ORIGIN_BY_HOST[hostname] ?? null;
}

export function crmV3SignUpUrlFor(hostname: string): string | null {
  const origin = crmV3OriginFor(hostname);
  return origin === null ? null : `${origin}/sign-up`;
}

/**
 * Sends a person CRM V3 is open to (and whose account has no store left on V2) to CRM V3
 * (`v3.access.v2Redirect`). Returns true while the answer is pending or the redirect is under way,
 * so V2 renders nothing meanwhile. Any error keeps the person on V2.
 */
export function useCrmV3Redirect(orgSlug: string): boolean {
  const clerk = useClerk();
  const v3Origin = typeof window === 'undefined' ? null : crmV3OriginFor(window.location.hostname);
  const redirect = trpc.v3.access.v2Redirect.useQuery(undefined, {
    enabled: v3Origin !== null && orgSlug.length > 0,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  const goesToV3 = v3Origin !== null && redirect.data?.redirectToV3 === true;

  useEffect(() => {
    if (goesToV3) {
      // Staging's Clerk development instance needs the session in the URL; production returns it unchanged.
      window.location.replace(clerk.buildUrlWithAuth(`${v3Origin}/org/${encodeURIComponent(orgSlug)}`));
    }
  }, [clerk, goesToV3, v3Origin, orgSlug]);

  if (v3Origin === null || orgSlug.length === 0) return false;
  return redirect.isPending || goesToV3;
}
